import { queryOptions, useQuery } from '@tanstack/react-query';
import { cloneDeep } from 'lodash-es';
import { z } from 'zod';
import { generateResponseScheme, type RespondedWith } from '@/models/response';
import { useAuthStore } from '@/stores/auth';
import { fetcher } from '@/utils/fetcher';
import { fileSizeRegex, parseFileSize } from '@/utils/file-size';

const BYTES_IN_MEGABYTE = 1_000_000;

interface SystemSettingFileLimit {
  admin: number;
  user: number;
}

interface SystemSettingFileLimits {
  file: SystemSettingFileLimit;
  video: SystemSettingFileLimit;
  audio: SystemSettingFileLimit;
  image: SystemSettingFileLimit;
  avatar: SystemSettingFileLimit;
  other: number;
}

interface SystemSetting {
  uploadLimits: SystemSettingFileLimits;
  uploadChunkSize: number;
  sampleRates: {
    traces: number;
    profiles: number;
  };
  enableInternalUserControl: boolean;
  enableRecordUpload: boolean;
}

export type FileLimitType = keyof SystemSettingFileLimits;

const defaultSystemSetting: SystemSetting = {
  uploadChunkSize: 1024 * 1024 * 6,
  uploadLimits: {
    other: 10 * BYTES_IN_MEGABYTE,
    file: {
      admin: 0,
      user: 0,
    },
    video: {
      admin: 0,
      user: 0,
    },
    audio: {
      admin: 0,
      user: 0,
    },
    image: {
      admin: 0,
      user: 0,
    },
    avatar: {
      admin: 0,
      user: 0,
    },
  },
  sampleRates: {
    traces: 0,
    profiles: 0,
  },
  enableInternalUserControl: true,
  enableRecordUpload: false,
};

async function fetchSystemSetting(signal?: AbortSignal): Promise<SystemSetting> {
  const response = await fetcher<RespondedWith<SystemSettingResponseType>>('/GetSystemSetting', undefined, {
    signal,
    schema: generateResponseScheme(systemSettingScheme),
    validResponseCodes: [0],
  });

  const result = cloneDeep(defaultSystemSetting);

  if (response.Data) {
    const {
      fileSize,
      ChunkSize,
      FileLimits,
      ProfilesSampleRate,
      TracesSampleRate,
      EnableInternalUserControl,
      EnableRecordUpload,
    } = response.Data;

    result.uploadChunkSize = ChunkSize;
    result.uploadLimits.other = parseFileSize(fileSize);

    if (FileLimits) {
      result.uploadLimits.file.admin = FileLimits.FileLimit * BYTES_IN_MEGABYTE;
      result.uploadLimits.file.user = FileLimits.UserFileLimit * BYTES_IN_MEGABYTE;
      result.uploadLimits.image.admin = FileLimits.ImageFileLimit * BYTES_IN_MEGABYTE;
      result.uploadLimits.image.user = FileLimits.UserImageFileLimit * BYTES_IN_MEGABYTE;
      result.uploadLimits.video.admin = FileLimits.VideoFileLimit * BYTES_IN_MEGABYTE;
      result.uploadLimits.video.user = FileLimits.UserVideoFileLimit * BYTES_IN_MEGABYTE;
      result.uploadLimits.audio.admin = FileLimits.AudioFileLimit * BYTES_IN_MEGABYTE;
      result.uploadLimits.audio.user = FileLimits.UserAudioFileLimit * BYTES_IN_MEGABYTE;
      result.uploadLimits.avatar.admin = FileLimits.HeadImageFileLimit * BYTES_IN_MEGABYTE;
      result.uploadLimits.avatar.user = FileLimits.HeadImageFileLimit * BYTES_IN_MEGABYTE;
    }

    result.sampleRates.traces = TracesSampleRate;
    result.sampleRates.profiles = ProfilesSampleRate;

    result.enableInternalUserControl = EnableInternalUserControl;
    result.enableRecordUpload = EnableRecordUpload;

    useAuthStore.getState().updateTraceRate(result.sampleRates.traces);
  }

  return result;
}

export const systemSettingQueryOptions = queryOptions({
  queryKey: ['/GetSystemSetting'],
  queryFn: ({ signal }) => fetchSystemSetting(signal),
  placeholderData: cloneDeep(defaultSystemSetting),
});

export default function useSystemSetting() {
  const query = useQuery(systemSettingQueryOptions);

  const limit = query.data?.uploadLimits?.other ?? 10 * BYTES_IN_MEGABYTE;

  const getFileSizeLimit = (type: string | null | undefined, isAdmin = false): number => {
    const limits = query.data?.uploadLimits;

    if (!limits) {
      return limit;
    }

    switch (type) {
      case 'file':
        return isAdmin ? limits.file.admin : limits.file.user;
      case 'video':
        return isAdmin ? limits.video.admin : limits.video.user;
      case 'audio':
        return isAdmin ? limits.audio.admin : limits.audio.user;
      case 'image':
        return isAdmin ? limits.image.admin : limits.image.user;
      case 'avatar':
      case 'head':
        return isAdmin ? limits.avatar.admin : limits.avatar.user;
      default:
        return limit;
    }
  };

  return {
    limit,
    getFileSizeLimit,
    ...query,
  };
}

const fileLimitScheme = z.object({
  FileLimit: z.number(),
  VideoFileLimit: z.number(),
  AudioFileLimit: z.number(),
  ImageFileLimit: z.number(),
  UserFileLimit: z.number(),
  UserVideoFileLimit: z.number(),
  UserAudioFileLimit: z.number(),
  UserImageFileLimit: z.number(),
  HeadImageFileLimit: z.number(),
});

const systemSettingScheme = z.object({
  fileSize: z.string().regex(fileSizeRegex),
  FileLimits: fileLimitScheme.nullish(),
  ProfilesSampleRate: z.number(),
  TracesSampleRate: z.number(),
  ChunkSize: z.number().default(1024 * 1024 * 6),
  EnableInternalUserControl: z.boolean().default(true),
  EnableRecordUpload: z.boolean().default(false),
});

type SystemSettingResponseType = z.infer<typeof systemSettingScheme>;
